import Warning from '@mui/icons-material/Warning';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { ImageBox } from '../../../components/BoxedImage';
import { CameraImageViewer } from '../../../components/CameraImageViewer';
import { ImageItemDetailsDialog } from '../../../components/ImageItemDetailsDialog';
import { ModFilters } from '../../../components/ModFilters';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { useImagePreloader } from '../../../hooks/useImagePreloader';
import { ApiService } from '../../../services/api';
import {
  BBox,
  CameraDetails,
  CameraImageResponse,
  CameraPog,
  ClassificationType,
  PogBBox,
  ProductRecognitionCropScanResponse,
  Rect,
  UpcCorrectionCanceledReason,
} from '../../../types';
import { areBoxOverlapping } from '../../../utils/geometry';
import { CandidateDetailHeader } from '../components/CandidateDetailHeader';
import { CropScansList } from '../components/CropScansList';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';
import {
  OutsSelectionEmbeddingUpdate,
  useOutsSelectionScanning,
} from '../utils/useOutsSelectionScanning';
import { EmbeddingTypeFilter } from './EmbeddingTypeFilter';
import { PageLoadingSpinner } from './PageLoadingSpinner';

const COLORS = {
  SELECTED_BOX: '#FFFF00', // Yellow - user selected for matching
  UNSELECTED_BOX: '#FF0000', // Red - no match
  UPDATED_BOX: '#4CAF50', // Green - user matched
  MODEL_MATCHED_BOX: '#2196F3', // Blue - model matched
} as const;

// Overlap detection threshold (40% minimum overlap required)
const OVERLAP_THRESHOLD = 0.4;

// Selection state types
interface SelectionState {
  selectedBoxes: Set<string>;
  highlightedPogBox: PogBBox | null;
  filterType: ClassificationType | 'all';
  pendingSaves: Map<
    number,
    {
      upc: string;
      ilItemId: number;
      productName: string | null;
      productId: number;
      shelfColumnLabel?: string; // Format: "mod-shelf-column" e.g., "1-2-3"
    }
  >;
}

type SelectionAction =
  | { type: 'SET_SELECTED_BOXES'; payload: Set<string> }
  | { type: 'CLEAR_SELECTED_BOXES' }
  | { type: 'TOGGLE_SELECTED_BOX'; payload: string }
  | { type: 'SET_HIGHLIGHTED_POG_BOX'; payload: PogBBox | null }
  | { type: 'SET_FILTER_TYPE'; payload: ClassificationType | 'all' }
  | { type: 'REMOVE_PENDING_SAVE'; payload: number }
  | {
      type: 'ADD_PENDING_SAVES';
      payload: {
        cropScans: ProductRecognitionCropScanResponse[];
        pogBox: {
          upc: string;
          ilItemId: number;
          productName: string | null;
          productId: number;
          shelfColumnLabel?: string; // Format: "mod-shelf-column" e.g., "1-2-3"
        };
      };
    }
  | { type: 'CLEAR_PENDING_SAVES' };

// Selection reducer
function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'SET_SELECTED_BOXES':
      return { ...state, selectedBoxes: action.payload };
    case 'CLEAR_SELECTED_BOXES':
      return { ...state, selectedBoxes: new Set() };
    case 'TOGGLE_SELECTED_BOX': {
      const newSelected = new Set(state.selectedBoxes);
      if (newSelected.has(action.payload)) {
        newSelected.delete(action.payload);
      } else {
        newSelected.add(action.payload);
      }
      return { ...state, selectedBoxes: newSelected };
    }
    case 'SET_HIGHLIGHTED_POG_BOX':
      return { ...state, highlightedPogBox: action.payload };
    case 'SET_FILTER_TYPE':
      return { ...state, filterType: action.payload };
    case 'ADD_PENDING_SAVES': {
      const newPendingSaves = new Map(state.pendingSaves);
      action.payload.cropScans.forEach((cropScan) => {
        newPendingSaves.set(cropScan.product_crop_scan_id, {
          upc: action.payload.pogBox.upc,
          ilItemId: action.payload.pogBox.ilItemId,
          productName: action.payload.pogBox.productName,
          productId: action.payload.pogBox.productId,
          shelfColumnLabel: action.payload.pogBox.shelfColumnLabel,
        });
      });
      return { ...state, pendingSaves: newPendingSaves };
    }
    case 'CLEAR_PENDING_SAVES':
      return { ...state, pendingSaves: new Map() };
    case 'REMOVE_PENDING_SAVE': {
      const newPendingSaves = new Map(state.pendingSaves);
      newPendingSaves.delete(action.payload);
      return { ...state, pendingSaves: newPendingSaves };
    }
    default:
      return state;
  }
}

interface OutsSelectionProps {
  imageId: string;
  cameraImage: CameraImageResponse;
}

export const OutsSelection: React.FC<OutsSelectionProps> = ({ imageId, cameraImage }) => {
  const [searchParams] = useSearchParams();
  const initialUpc = searchParams.get('upc');

  const navigate = useNavigate();

  const [selectionState, dispatchSelection] = useReducer(selectionReducer, {
    highlightedPogBox: null,
    selectedBoxes: new Set<string>(),
    filterType: 'carton' as ClassificationType | 'all',
    pendingSaves: new Map<
      number,
      {
        upc: string;
        ilItemId: number;
        productName: string | null;
        productId: number;
        shelfColumnLabel?: string; // Format: "mod-shelf-column" e.g., "1-2-3"
      }
    >(),
  });
  const { selectedBoxes, filterType, highlightedPogBox, pendingSaves } = selectionState;
  const { hasInternalAccess, token, legacyToken } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [isLoadingNavigation, setIsLoadingNavigation] = useState(false);
  const [cameras, setCameras] = useState<CameraDetails[]>([]);
  const [showShelfColumnLabels, setShowShelfColumnLabels] = useState(false);
  const [showEmbeddingsCount, setShowEmbeddingsCount] = useState(true);
  const [enabledMods, setEnabledMods] = useState<Set<string>>(new Set());

  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);
  const [auditReason, setAuditReason] = useState<string>('');
  const [showDetectionDialog, setShowDetectionDialog] = useState(false);
  const [detectionForModal, setDetectionForModal] = useState<{ item: PogBBox | ImageBox } | null>(
    null
  );

  const chainId = cameraImage.chainId;
  const storeId = cameraImage.storeId;
  const storeName = cameraImage.storeName;
  const cameraId = cameraImage.cameraId;
  const initialBbox = useMemo(() => {
    const box = searchParams.get('box')?.split(',')?.map(Number);

    return box?.length === 4 ? (box as Rect) : undefined;
  }, [searchParams]);

  // Use the product recognition scanning hook
  const {
    cropScans,
    productRecognitionData,
    isRunningModel,
    isFetchingCropScans,
    isGeneratingCropScans,
    cropScansError,
    sessionStarted,
    startSession,
    createEmbeddings,
  } = useOutsSelectionScanning(imageId as string, hasInternalAccess);

  const [confidenceThresholdPercent, setConfidenceThresholdPercent] = useState(80);
  const confidenceThreshold = confidenceThresholdPercent / 100;

  // Default filter to 'all' when there are no carton boxes
  useEffect(() => {
    if (productRecognitionData && (productRecognitionData.cartonBoxes?.length ?? 0) === 0) {
      dispatchSelection({ type: 'SET_FILTER_TYPE', payload: 'all' });
    }
  }, [productRecognitionData]);

  // Track previous sessionStarted value to detect false -> true transitions
  const prevSessionStarted = useRef(sessionStarted);

  const blocker = useBlocker(pendingSaves.size > 0);

  const fetchCameras = useCallback(async () => {
    if (!storeName) {
      return null;
    }

    try {
      const data = await ApiService.fetchCameras(token, storeName, {
        useCache: true,
      });
      setCameras(data);
    } catch (err) {
      showErrorToast('Failed to fetch cameras');
      return null;
    }
  }, [token, storeName, showErrorToast]);

  // Get current image from cameraImage (equivalent to currentImage in SelectedCameraView)
  const currentImage = cameraImage.image;

  const cameraTimeline = useMemo(
    () => ({
      images: [cameraImage.image],
      templates: cameraImage.templates,
      pogs: cameraImage.pogs,
    }),
    [cameraImage]
  );

  // Use the image preloader hook
  const {
    preloadedImages,
    preloadedImagesB64,
    isLoading: isLoadingImages,
  } = useImagePreloader({
    cameraTimeline,
    authToken: legacyToken,
    pogImageUri: undefined,
  });

  // Find the selected camera based on cameraId from query params
  const selectedCamera = useMemo(() => {
    if (!cameraId || !cameras || cameras.length === 0) {
      return null;
    }
    return cameras.find((cam) => cam.cameraId === cameraId) ?? null;
  }, [cameraId, cameras]);

  const allTagNames = useMemo(() => {
    if (!selectedCamera) return [];
    return selectedCamera.mods.map((mod) => mod.tagName);
  }, [selectedCamera]);

  useEffect(() => {
    setEnabledMods(new Set(allTagNames));
  }, [allTagNames]);

  const toggleMod = useCallback((tagName: string) => {
    setEnabledMods((prev) => {
      const next = new Set(prev);
      if (next.has(tagName)) {
        next.delete(tagName);
      } else {
        next.add(tagName);
      }
      return next;
    });
  }, []);

  // Get POG data for the current image based on il_section_ids
  const currentPogs = useMemo(() => {
    const allPogs = cameraImage.pogs ? Object.values(cameraImage.pogs) : [];

    return allPogs
      .filter((pog): pog is CameraPog => pog !== undefined)
      .sort((a, b) => {
        if (!a || !b) return 0;
        return allTagNames.indexOf(a.tagName) - allTagNames.indexOf(b.tagName);
      });
  }, [cameraImage, allTagNames]);

  // Map of synthetic IDs to corrected UPCs (previously matched by a user via corrections)
  const userCorrectedMap = useMemo(() => {
    if (!productRecognitionData?.corrections) return new Map<number, string>();
    const map = new Map<number, string>();

    for (const correction of productRecognitionData.corrections) {
      const syntheticId =
        correction.shapeList === 'carton_boxes'
          ? 10000 + correction.index + 1
          : correction.index + 1;
      map.set(syntheticId, correction.upc);
    }
    return map;
  }, [productRecognitionData]);

  // Build set of UPCs confidently matched by product recognition detections (via finalUpc + confidence)
  // Exclude detections that the user has already manually matched (in pendingSaves)
  const matchedPogUpcs = useMemo(() => {
    if (!productRecognitionData) return new Set<string>();
    const matched = new Set<string>();

    const indexedBoxes = [
      ...(productRecognitionData.productBoxes ?? []).map((box, i) => ({ box, syntheticId: i + 1 })),
      ...(productRecognitionData.cartonBoxes ?? []).map((box, i) => ({
        box,
        syntheticId: 10000 + i + 1,
      })),
    ];

    for (const { box, syntheticId } of indexedBoxes) {
      if (pendingSaves.has(syntheticId)) continue;
      const correctedUpc = userCorrectedMap.get(syntheticId);
      if (correctedUpc) {
        matched.add(correctedUpc);
      } else if (
        box.finalUpc &&
        (box.vizSimCandidates?.[0]?.similarity ?? 0) >= confidenceThreshold
      ) {
        matched.add(box.finalUpc);
      }
    }
    return matched;
  }, [productRecognitionData, confidenceThreshold, pendingSaves, userCorrectedMap]);

  // Set of synthetic IDs that have a confident model match (finalUpc + confidence >= threshold)
  const modelMatchedIds = useMemo(() => {
    if (!productRecognitionData) return new Set<number>();
    const ids = new Set<number>();

    const indexedBoxes = [
      ...(productRecognitionData.productBoxes ?? []).map((box, i) => ({ box, syntheticId: i + 1 })),
      ...(productRecognitionData.cartonBoxes ?? []).map((box, i) => ({
        box,
        syntheticId: 10000 + i + 1,
      })),
    ];

    for (const { box, syntheticId } of indexedBoxes) {
      if (box.finalUpc && (box.vizSimCandidates?.[0]?.similarity ?? 0) >= confidenceThreshold) {
        ids.add(syntheticId);
      }
    }
    return ids;
  }, [productRecognitionData, confidenceThreshold]);

  // Mark POG bboxes that are already confidently matched by detections
  const filteredPogs = useMemo(() => {
    return currentPogs
      .filter((pog) => enabledMods.size === 0 || enabledMods.has(pog.tagName))
      .map((pog) => ({
        ...pog,
        bboxes: pog.bboxes.map((bbox) => ({
          ...bbox,
          modelMatched: matchedPogUpcs.has(bbox.upc),
        })),
      }));
  }, [currentPogs, matchedPogUpcs, enabledMods]);

  // Precompute POG previews by product_id so we can quickly show the matching POG image per crop scan
  const pogPreviewsByProductId = useMemo(() => {
    if (!currentPogs || currentPogs.length === 0)
      return {} as Record<
        number,
        {
          pogBox: PogBBox;
        }
      >;

    const previews: Record<
      number,
      {
        pogBox: PogBBox;
      }
    > = {};

    currentPogs.forEach((pog) => {
      (pog.bboxes ?? []).forEach((bbox) => {
        const productId = bbox.productId;
        if (!productId || previews[productId]) {
          return;
        }

        previews[productId] = {
          pogBox: bbox,
        };
      });
    });

    return previews;
  }, [currentPogs]);

  // Enrich crop scans with pending save data and shelf-column labels
  const filteredCropScans = useMemo(() => {
    const filteredScans = cropScans
      .map((scan) => {
        const pendingSave = pendingSaves.get(scan.product_crop_scan_id);

        // Get shelf-column label from pendingSave or by looking up completed scan
        let shelfColumnLabel: string | undefined;
        if (pendingSave?.shelfColumnLabel) {
          shelfColumnLabel = pendingSave.shelfColumnLabel;
        } else if (scan.barcode && scan.normalized_upc && currentPogs.length > 0) {
          // For completed scans, find matching POG box by UPC and build label
          for (let pogIndex = 0; pogIndex < currentPogs.length; pogIndex++) {
            const pog = currentPogs[pogIndex];
            const matchingBox = pog?.bboxes.find((box) => box.upc === scan.normalized_upc);
            if (
              matchingBox &&
              matchingBox.shelf !== undefined &&
              matchingBox.column !== undefined
            ) {
              // Only include mod number if there are multiple POGs
              shelfColumnLabel =
                currentPogs.length > 1
                  ? `${pogIndex + 1}-${matchingBox.shelf}-${matchingBox.column}`
                  : `${matchingBox.shelf}-${matchingBox.column}`;
              break;
            }
          }
        }

        if (!pendingSave) {
          // If corrected via corrections, treat as matched with the corrected UPC
          const correctedUpc = userCorrectedMap.get(scan.product_crop_scan_id);
          if (correctedUpc) {
            // Look up matching POG box to get product_id and product_name
            let matchingPogBox: PogBBox | undefined;
            let matchingPogIndex = -1;
            for (let pi = 0; pi < currentPogs.length; pi++) {
              const pog = currentPogs[pi];
              matchingPogBox = pog?.bboxes.find((box) => box.upc === correctedUpc);
              if (matchingPogBox) {
                matchingPogIndex = pi;
                break;
              }
            }
            // Compute shelfColumnLabel from matched POG box if not already set
            let correctedShelfColumnLabel = shelfColumnLabel;
            if (
              !correctedShelfColumnLabel &&
              matchingPogBox &&
              matchingPogBox.shelf !== undefined &&
              matchingPogBox.column !== undefined
            ) {
              correctedShelfColumnLabel =
                currentPogs.length > 1
                  ? `${matchingPogIndex + 1}-${matchingPogBox.shelf}-${matchingPogBox.column}`
                  : `${matchingPogBox.shelf}-${matchingPogBox.column}`;
            }
            return {
              ...scan,
              barcode: correctedUpc,
              normalized_upc: correctedUpc,
              product_id: matchingPogBox?.productId,
              product_name: matchingPogBox?.name ?? undefined,
              isPending: false,
              shelfColumnLabel: correctedShelfColumnLabel,
            };
          }
          return { ...scan, isPending: false, shelfColumnLabel };
        }

        return {
          ...scan,
          product_id: pendingSave.productId ?? undefined,
          product_name: pendingSave.productName ?? undefined,
          barcode: pendingSave.upc,
          isPending: true,
          shelfColumnLabel,
        };
      })
      .sort((a, b) => {
        return Number(b.isPending) - Number(a.isPending);
      });
    return filteredScans;
  }, [cropScans, pendingSaves, currentPogs, userCorrectedMap]);

  const sortedByTypeCropScans = useMemo(() => {
    const typeFiltered = filteredCropScans.filter(
      (scan) => filterType === 'all' || scan.classification_type === filterType
    );

    if (filterType !== 'all') {
      return typeFiltered;
    }

    return [...typeFiltered].sort((a, b) => {
      if (a.classification_type === 'product' && b.classification_type !== 'product') {
        return 1;
      }
      if (a.classification_type !== 'product' && b.classification_type === 'product') {
        return -1;
      }
      return 0;
    });
  }, [filteredCropScans, filterType]);

  const selectedIlItemIds = useMemo(() => {
    const selectedIds = new Set<number>();
    const matchedProductIds = new Set<number>();

    // Add POG boxes that have ilItemIds in pendingSaves
    pendingSaves.forEach((pogData) => {
      selectedIds.add(pogData.ilItemId);
      if (pogData.productId) matchedProductIds.add(pogData.productId);
    });

    cropScans
      .filter((scan) => scan.barcode)
      .forEach((scan) => {
        currentPogs.forEach((pog) => {
          pog?.bboxes.forEach((box) => {
            if (box.productId === scan.product_id && box.upc === scan.normalized_upc) {
              selectedIds.add(box.ilItemId);
              if (box.productId) matchedProductIds.add(box.productId);
            }
          });
        });
      });

    // Include POG items matched via corrections
    for (const [, correctedUpc] of userCorrectedMap) {
      for (const pog of currentPogs) {
        const matchingBox = pog?.bboxes.find((b) => b.upc === correctedUpc);
        if (matchingBox) {
          selectedIds.add(matchingBox.ilItemId);
          if (matchingBox.productId) matchedProductIds.add(matchingBox.productId);
          break;
        }
      }
    }

    // Include all POG boxes that share a productId with any matched box
    if (matchedProductIds.size > 0) {
      for (const pog of currentPogs) {
        for (const box of pog?.bboxes ?? []) {
          if (box.productId && matchedProductIds.has(box.productId)) {
            selectedIds.add(box.ilItemId);
          }
        }
      }
    }

    return selectedIds;
  }, [cropScans, currentPogs, pendingSaves, userCorrectedMap]);

  // Create filtered bboxes from crop scans
  const cropScansFilteredBboxes = useMemo(() => {
    if (!sortedByTypeCropScans || sortedByTypeCropScans.length === 0) return [];

    return sortedByTypeCropScans.map((cropScan) => {
      const boxId = cropScan.product_crop_scan_id.toString();
      const isSelected = selectedBoxes.has(boxId);

      const [x1, y1, width, height] = cropScan.bbox;
      const x2 = x1 + width;
      const y2 = y1 + height;
      const isCompleted = !!cropScan.barcode;

      // Get shelf-column label - first check pendingSaves, then look up from POG if already completed
      let shelfColumnLabel: string | undefined;
      const pendingSave = pendingSaves.get(cropScan.product_crop_scan_id);

      if (pendingSave?.shelfColumnLabel) {
        // Use label from pending save
        shelfColumnLabel = pendingSave.shelfColumnLabel;
      } else if (isCompleted && cropScan.normalized_upc && currentPogs.length > 0) {
        // For completed scans, find matching POG box by UPC and build label
        for (let pogIndex = 0; pogIndex < currentPogs.length; pogIndex++) {
          const pog = currentPogs[pogIndex];
          const matchingBox = pog?.bboxes.find((box) => box.upc === cropScan.normalized_upc);
          if (matchingBox && matchingBox.shelf !== undefined && matchingBox.column !== undefined) {
            // Only include mod number if there are multiple POGs
            shelfColumnLabel =
              currentPogs.length > 1
                ? `${pogIndex + 1}-${matchingBox.shelf}-${matchingBox.column}`
                : `${matchingBox.shelf}-${matchingBox.column}`;
            break;
          }
        }
      }

      return {
        bboxId: cropScan.product_crop_scan_id,
        tagName: `crop_scan_${cropScan.product_crop_scan_id}`,
        x1,
        y1,
        x2,
        y2,
        productId: cropScan.product_id ?? 0,
        itemNumber: '0',
        upc: cropScan.normalized_upc ?? '',
        name: null,
        imageUri: null,
        modCoverageId: 0,
        templateId: 0,
        color: isSelected
          ? COLORS.SELECTED_BOX
          : isCompleted || userCorrectedMap.has(cropScan.product_crop_scan_id)
            ? COLORS.UPDATED_BOX
            : modelMatchedIds.has(cropScan.product_crop_scan_id)
              ? COLORS.MODEL_MATCHED_BOX
              : COLORS.UNSELECTED_BOX,
        // Add the complete label
        shelfColumnLabel,
      };
    });
  }, [
    selectedBoxes,
    sortedByTypeCropScans,
    pendingSaves,
    currentPogs,
    modelMatchedIds,
    userCorrectedMap,
  ]);

  const totalBboxesPerPog = useMemo(() => {
    return filteredPogs.map(
      (pog) =>
        pog.bboxes.filter(
          (bbox) => selectedIlItemIds.has(bbox.ilItemId) || !matchedPogUpcs.has(bbox.upc)
        ).length
    );
  }, [filteredPogs, selectedIlItemIds, matchedPogUpcs]);

  // Count unmatched POG bboxes per POG index (those not matched by user or model)
  const outsCountPerPog = useMemo(() => {
    return filteredPogs.map(
      (pog) =>
        pog.bboxes.filter(
          (bbox) => !matchedPogUpcs.has(bbox.upc) && !selectedIlItemIds.has(bbox.ilItemId)
        ).length
    );
  }, [filteredPogs, matchedPogUpcs, selectedIlItemIds]);

  const selectedCropScans = useMemo(() => {
    return filteredCropScans.filter((cropScan) => {
      const boxId = cropScan.product_crop_scan_id.toString();
      return selectedBoxes.has(boxId);
    });
  }, [filteredCropScans, selectedBoxes]);

  const handleFilterChange = useCallback(
    (value: ClassificationType | 'all') => {
      dispatchSelection({
        type: 'SET_FILTER_TYPE',
        payload: value,
      });
    },
    [dispatchSelection]
  );

  const clearCurrentSelections = useCallback(() => {
    dispatchSelection({ type: 'SET_HIGHLIGHTED_POG_BOX', payload: null });
    dispatchSelection({ type: 'CLEAR_SELECTED_BOXES' });
  }, []);

  // Match crop scans with a POG box — creates pending saves
  const makeMatch = useCallback(
    (pogBox: PogBBox, cropScansToMatch: ProductRecognitionCropScanResponse[]) => {
      if (!pogBox.ilItemId || cropScansToMatch.length === 0) return;

      // Build the shelf-column label with mod index
      let shelfColumnLabel: string | undefined;
      if (pogBox.shelf !== undefined && pogBox.column !== undefined && currentPogs.length > 0) {
        const modIndex = currentPogs.findIndex((pog) =>
          pog?.bboxes.some((box) => box.ilItemId === pogBox.ilItemId)
        );
        if (modIndex !== -1) {
          shelfColumnLabel =
            currentPogs.length > 1
              ? `${modIndex + 1}-${pogBox.shelf}-${pogBox.column}`
              : `${pogBox.shelf}-${pogBox.column}`;
        }
      }

      dispatchSelection({
        type: 'ADD_PENDING_SAVES',
        payload: {
          cropScans: cropScansToMatch,
          pogBox: {
            upc: pogBox.upc,
            ilItemId: pogBox.ilItemId,
            productName: pogBox.name,
            productId: pogBox.productId,
            shelfColumnLabel,
          },
        },
      });

      clearCurrentSelections();

      const productInfo = [
        `UPC: ${pogBox.upc}`,
        pogBox.productId ? `Product ID: ${pogBox.productId}` : null,
        pogBox.name ? `Name: ${pogBox.name}` : null,
      ]
        .filter(Boolean)
        .join(', ');

      showSuccessToast(
        `${cropScansToMatch.length} item${
          cropScansToMatch.length > 1 ? 's' : ''
        } matched with ${productInfo}`
      );
    },
    [dispatchSelection, clearCurrentSelections, showSuccessToast, currentPogs]
  );

  const handleBoxClick = useCallback(
    (bbox: BBox) => {
      // If a POG box is highlighted, clicking a crop makes a match
      if (highlightedPogBox) {
        const cropScan = filteredCropScans.find((cs) => cs.product_crop_scan_id === bbox.bboxId);
        if (cropScan) {
          makeMatch(highlightedPogBox, [cropScan]);
          return;
        }
      }

      // Otherwise toggle crop selection
      dispatchSelection({
        type: 'TOGGLE_SELECTED_BOX',
        payload: bbox.bboxId.toString(),
      });
    },
    [highlightedPogBox, filteredCropScans, makeMatch, dispatchSelection]
  );

  const handlePogBoxClick = useCallback(
    (pogBox: PogBBox) => {
      // If crops are selected, clicking a POG makes a match
      if (selectedCropScans.length > 0) {
        makeMatch(pogBox, selectedCropScans);
        return;
      }

      // Otherwise select/toggle this POG box
      if (highlightedPogBox?.ilItemId === pogBox.ilItemId) {
        dispatchSelection({ type: 'SET_HIGHLIGHTED_POG_BOX', payload: null });
      } else {
        dispatchSelection({ type: 'SET_HIGHLIGHTED_POG_BOX', payload: pogBox });
      }
    },
    [selectedCropScans, makeMatch, highlightedPogBox, dispatchSelection]
  );

  // Find POG box that matches the UPC from URL params
  const pogBoxMatchingUpc = useMemo(() => {
    if (!initialUpc || !currentPogs || currentPogs.length === 0) return null;

    // Find POG box that matches the UPC
    for (const pog of currentPogs) {
      if (pog?.bboxes) {
        const matchingBox = pog.bboxes.find((box: PogBBox) => box.upc === initialUpc);
        if (matchingBox) {
          return matchingBox;
        }
      }
    }
    return null;
  }, [initialUpc, currentPogs]);

  useEffect(() => {
    // Only trigger when sessionStarted changes from false to true
    const sessionJustStarted = !prevSessionStarted.current && sessionStarted;

    if (!sessionJustStarted || !initialBbox) {
      return;
    }

    if (pogBoxMatchingUpc) {
      dispatchSelection({
        type: 'SET_HIGHLIGHTED_POG_BOX',
        payload: pogBoxMatchingUpc,
      });
    }

    if (filteredCropScans.length === 0) {
      return;
    }

    prevSessionStarted.current = sessionStarted;

    const overlappingCropScans = new Set<string>();

    filteredCropScans.forEach((cropScan) => {
      if (cropScan.barcode) {
        return;
      }

      if (areBoxOverlapping(initialBbox, cropScan.bbox, OVERLAP_THRESHOLD)) {
        overlappingCropScans.add(cropScan.product_crop_scan_id.toString());
      }
    });

    dispatchSelection({
      type: 'SET_SELECTED_BOXES',
      payload: overlappingCropScans,
    });
  }, [filteredCropScans, initialBbox, initialUpc, pogBoxMatchingUpc, sessionStarted]);

  // Clear selected boxes when session ends
  useEffect(() => {
    if (!sessionStarted) {
      dispatchSelection({ type: 'CLEAR_SELECTED_BOXES' });
    }
  }, [sessionStarted]);

  const handleDeleteCropScan = useCallback(
    async (productCropScanId: number) => {
      if (!pendingSaves.has(productCropScanId)) {
        return;
      }

      dispatchSelection({
        type: 'REMOVE_PENDING_SAVE',
        payload: productCropScanId,
      });
    },
    [pendingSaves]
  );

  // Function to handle saving selected crop scans
  const handleCreateEmbeddings = useCallback(
    async (canceled?: UpcCorrectionCanceledReason) => {
      const directUpdates: OutsSelectionEmbeddingUpdate[] = [...pendingSaves.entries()]
        .map(([syntheticId, pogData]) => {
          const cropScan = cropScans.find((cs) => cs.product_crop_scan_id === syntheticId);
          return {
            syntheticId,
            bbox: cropScan?.bbox as [number, number, number, number],
            barcode: pogData.upc,
            normalizedUpc: pogData.upc,
            productId: pogData.productId,
          };
        })
        .filter((update) => update.bbox);

      // Expand matches: for matched products, also include containing cartons;
      // for matched cartons, also include contained products
      const additionalUpdates: OutsSelectionEmbeddingUpdate[] = [];
      if (productRecognitionData) {
        const productBoxes = productRecognitionData.productBoxes ?? [];
        const cartonBoxes = productRecognitionData.cartonBoxes ?? [];
        const matchedSyntheticIds = new Set(directUpdates.map((u) => u.syntheticId));

        for (const update of directUpdates) {
          const isCarton = update.syntheticId > 10000;

          if (!isCarton) {
            // Matched product: find containing carton by center point
            const productIndex = update.syntheticId - 1;
            const productBox = productBoxes[productIndex];
            if (!productBox) continue;
            const cx = (productBox.x1 + productBox.x2) / 2;
            const cy = (productBox.y1 + productBox.y2) / 2;

            for (let ci = 0; ci < cartonBoxes.length; ci++) {
              const cartonSyntheticId = 10000 + ci + 1;
              if (matchedSyntheticIds.has(cartonSyntheticId)) continue;
              const carton = cartonBoxes[ci];
              if (cx >= carton.x1 && cx <= carton.x2 && cy >= carton.y1 && cy <= carton.y2) {
                additionalUpdates.push({
                  syntheticId: cartonSyntheticId,
                  bbox: carton.shape,
                  barcode: update.barcode,
                  normalizedUpc: update.normalizedUpc,
                  productId: update.productId,
                });
                matchedSyntheticIds.add(cartonSyntheticId);
                break;
              }
            }
          } else {
            // Matched carton: find contained products by center point
            const cartonIndex = update.syntheticId - 10001;
            const cartonBox = cartonBoxes[cartonIndex];
            if (!cartonBox) continue;

            for (let pi = 0; pi < productBoxes.length; pi++) {
              const productSyntheticId = pi + 1;
              if (matchedSyntheticIds.has(productSyntheticId)) continue;
              const product = productBoxes[pi];
              const cx = (product.x1 + product.x2) / 2;
              const cy = (product.y1 + product.y2) / 2;
              if (
                cx >= cartonBox.x1 &&
                cx <= cartonBox.x2 &&
                cy >= cartonBox.y1 &&
                cy <= cartonBox.y2
              ) {
                additionalUpdates.push({
                  syntheticId: productSyntheticId,
                  bbox: product.shape,
                  barcode: update.barcode,
                  normalizedUpc: update.normalizedUpc,
                  productId: update.productId,
                });
                matchedSyntheticIds.add(productSyntheticId);
              }
            }
          }
        }
      }

      const embeddingUpdates = [...directUpdates, ...additionalUpdates];

      // Apply UPC corrections first
      const inferenceId = productRecognitionData?.inferenceId;
      if (inferenceId && token) {
        const corrections = embeddingUpdates.map((update) => {
          const isCarton = update.syntheticId > 10000;
          return {
            shape_list: productRecognitionData?.isV2
              ? isCarton
                ? 'carton_polygons'
                : 'product_polygons'
              : isCarton
                ? 'carton_boxes'
                : 'product_boxes',
            index: isCarton ? update.syntheticId - 10001 : update.syntheticId - 1,
            upc: update.barcode,
          };
        });

        try {
          await ApiService.applyUpcCorrections(token, inferenceId, {
            corrections,
            confidence: Math.round(confidenceThresholdPercent) / 100,
            ...(canceled ? { canceled } : {}),
          });
        } catch (error) {
          showErrorToast(
            error instanceof Error ? error.message : 'Failed to apply UPC corrections'
          );
          return;
        }
      }

      if (embeddingUpdates.length > 0) {
        const isSuccess = await createEmbeddings(embeddingUpdates);

        if (isSuccess) {
          showSuccessToast(`Created ${embeddingUpdates.length} embeddings`);
        }
      } else {
        showSuccessToast('Image marked as audited');
      }

      dispatchSelection({ type: 'CLEAR_PENDING_SAVES' });
      // Refresh data after saving
      startSession();
    },
    [
      pendingSaves,
      cropScans,
      createEmbeddings,
      startSession,
      showSuccessToast,
      productRecognitionData,
      showErrorToast,
      token,
      confidenceThresholdPercent,
    ]
  );

  const titleActions = useMemo(
    () => (
      <div className="flex items-center gap-4">
        <FormControlLabel
          control={
            <Switch
              checked={showShelfColumnLabels}
              onChange={(e) => setShowShelfColumnLabels(e.target.checked)}
              size="small"
            />
          }
          label="Show Labels"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showEmbeddingsCount}
              onChange={(e) => setShowEmbeddingsCount(e.target.checked)}
              size="small"
            />
          }
          label="Count Embeddings"
        />
        {hasInternalAccess && (
          <TextField
            label="Confidence"
            type="number"
            size="small"
            value={confidenceThresholdPercent}
            onChange={(e) => setConfidenceThresholdPercent(parseFloat(e.target.value) || 0)}
            inputProps={{ min: 0, max: 100, step: 1 }}
            sx={{ width: 90 }}
          />
        )}
        <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
      </div>
    ),
    [
      showShelfColumnLabels,
      showEmbeddingsCount,
      hasInternalAccess,
      confidenceThresholdPercent,
      filterType,
      handleFilterChange,
    ]
  );

  const pogHeaderActions = useMemo(
    () =>
      allTagNames.length > 0 ? (
        <ModFilters allTagNames={allTagNames} enabledMods={enabledMods} toggleMod={toggleMod} />
      ) : undefined,
    [allTagNames, enabledMods, toggleMod]
  );

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const navigateToRelativeImage = useCallback(
    async (rel: 'next' | 'prev') => {
      if (!token || !imageId) return;

      setIsLoadingNavigation(true);
      try {
        const data = await ApiService.fetchRelativeImage(token, imageId, rel);
        const newImageId = data.image?.imageId;
        if (!newImageId) {
          showErrorToast(`No ${rel === 'next' ? 'next' : 'previous'} image found`);
          return;
        }

        sessionStorage.setItem(`camera-image-${newImageId}`, JSON.stringify(data));

        navigate(`/outs-selection/${newImageId}`, { replace: true });
      } catch {
        showErrorToast(`No ${rel === 'next' ? 'next' : 'previous'} image found`);
      } finally {
        setIsLoadingNavigation(false);
      }
    },
    [token, imageId, navigate, showErrorToast, setIsLoadingNavigation]
  );

  const handlePreviousImage = useCallback(() => {
    navigateToRelativeImage('prev');
  }, [navigateToRelativeImage]);

  const handleNextImage = useCallback(() => {
    navigateToRelativeImage('next');
  }, [navigateToRelativeImage]);

  const handleDateChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const date = e.target.value;
      if (!date || !token || !storeName || !cameraId) return;

      setIsLoadingNavigation(true);
      try {
        const data = await ApiService.fetchCameraLatestImage(token, storeName, cameraId, date);
        const newImage = data.images?.[0];
        if (!newImage) {
          showErrorToast('No image found for selected date');
          return;
        }

        const cachedData: CameraImageResponse = {
          ...cameraImage,
          image: newImage,
          pogs: data.pogs,
          templates: data.templates,
        };
        sessionStorage.setItem(`camera-image-${newImage.imageId}`, JSON.stringify(cachedData));

        navigate(`/outs-selection/${newImage.imageId}`, { replace: true });
      } catch {
        showErrorToast('No image found for selected date');
      } finally {
        setIsLoadingNavigation(false);
      }
    },
    [token, storeName, cameraId, cameraImage, navigate, showErrorToast, setIsLoadingNavigation]
  );

  const currentImageDate = currentImage?.detectedAt ? currentImage.detectedAt.split('T')[0] : '';

  const isLatestImage = currentImage?.detectedAt
    ? Date.now() - new Date(currentImage.detectedAt).getTime() < 60 * 60 * 1000
    : false;

  const datePickerExtra = cameraId ? (
    <TextField
      type="date"
      size="small"
      value={currentImageDate}
      onChange={handleDateChange}
      disabled={isLoadingNavigation}
      inputProps={{ max: new Date().toISOString().split('T')[0] }}
      sx={{
        '& .MuiInputBase-root': { fontSize: '0.875rem', height: '30px' },
        '& .MuiInputBase-input': { padding: '4px 8px' },
      }}
    />
  ) : null;

  const handleAcceptSelection = useCallback(() => {
    if (highlightedPogBox) {
      handlePogBoxClick(highlightedPogBox);
    }
  }, [highlightedPogBox, handlePogBoxClick]);

  const handleBoxesSelected = useCallback(
    (selectedBboxes: BBox[]) => {
      // Convert selected BBox objects to their IDs
      const newSelectedIds = selectedBboxes.map((bbox) => bbox.bboxId.toString());

      // Merge with existing selected boxes
      const mergedSelectedBoxIds = new Set([
        ...selectedBoxes, // existing selected boxes
        ...newSelectedIds, // newly selected boxes
      ]);

      dispatchSelection({
        type: 'SET_SELECTED_BOXES',
        payload: mergedSelectedBoxIds,
      });
    },
    [dispatchSelection, selectedBoxes]
  );

  const closeDetectionDialog = useCallback(() => {
    setShowDetectionDialog(false);
  }, []);

  const handlePogBoxRightClick = useCallback((pogBox: PogBBox) => {
    setDetectionForModal({ item: pogBox });
    setShowDetectionDialog(true);
  }, []);

  const handleBoxRightClick = useCallback(
    (detection: PogBBox | ImageBox) => {
      // Look up the original ProductRecognitionBBox from productRecognitionData
      // The bboxId is a synthetic ID: productBoxes = index+1, cartonBoxes = 10000+index+1
      const bboxId = (detection as unknown as BBox).bboxId;
      if (bboxId && productRecognitionData) {
        let originalBox;
        if (bboxId > 10000) {
          const cartonIndex = bboxId - 10001;
          originalBox = productRecognitionData.cartonBoxes?.[cartonIndex];
        } else {
          const productIndex = bboxId - 1;
          originalBox = productRecognitionData.productBoxes?.[productIndex];
        }
        if (originalBox) {
          setDetectionForModal({
            item: originalBox as unknown as ImageBox,
          });
          setShowDetectionDialog(true);
          return;
        }
      }
      setDetectionForModal({ item: detection as PogBBox | ImageBox });
      setShowDetectionDialog(true);
    },
    [productRecognitionData]
  );

  useEffect(() => {
    fetchCameras();
  }, [fetchCameras]);

  useEffect(() => {
    if (cameraImage) {
      startSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraImage]);

  if (!imageId || !parseInt(imageId, 10)) {
    return null;
  }

  const preloadedImage = currentImage ? preloadedImagesB64[currentImage.imageUri] : undefined;

  return (
    <div className="h-screen bg-gray-50 p-4 flex flex-col">
      <div className="w-full mx-auto space-y-4 flex flex-col flex-1 min-h-0">
        <CandidateDetailHeader
          title="Outs Correction"
          storeName={storeName ?? null}
          upc={initialUpc}
          imageId={imageId}
          detectedAt={currentImage?.detectedAt}
          onBack={handleBack}
          showNavigation
          isLoadingNavigation={isLoadingNavigation}
          onPreviousImage={handlePreviousImage}
          onNextImage={handleNextImage}
          disableNextImage={isLatestImage}
          navigationExtra={datePickerExtra}
          showProductSelection={!!highlightedPogBox?.ilItemId && selectedCropScans.length > 0}
          onRejectSelection={clearCurrentSelections}
          onAcceptSelection={handleAcceptSelection}
        />

        <UnsavedChangesDialog
          open={blocker.state === 'blocked'}
          onCancel={() => blocker.reset?.()}
          onDiscard={() => blocker.proceed?.()}
        />

        {productRecognitionData?.updatedAt && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg flex items-center gap-2">
            <Warning className="text-yellow-600" />
            <span className="font-medium">This image was already reviewed.</span>
          </div>
        )}

        {!isRunningModel && !productRecognitionData?.inferenceId && !isLoadingImages && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
            <Warning className="text-red-600" />
            <span className="font-medium">This image is not workable</span>
          </div>
        )}

        {/* Image Viewer */}
        {preloadedImage && productRecognitionData && (
          <div className="bg-white rounded-lg shadow-md p-6 flex-1">
            {/* Image Display */}
            <div className="mb-4">
              <CameraImageViewer
                highlightedIlItemId={highlightedPogBox?.ilItemId}
                preloadedImage={preloadedImage}
                filteredBboxes={cropScansFilteredBboxes}
                boxActions={[]}
                productRecognitionDetections={[]}
                planogramComplianceActions={[]}
                showProductImages={false}
                onBoxClick={handleBoxClick}
                onBoxesSelected={handleBoxesSelected}
                titleActions={titleActions}
                currentPogs={filteredPogs}
                preloadedImagesB64={preloadedImagesB64}
                selectedIlItemIds={selectedIlItemIds}
                fallbackPogBoxColor={COLORS.UNSELECTED_BOX}
                selectedPogBoxColor={COLORS.UPDATED_BOX}
                planogramStatusMap={new Map()}
                onPogBoxClick={handlePogBoxClick}
                onPogBoxRightClick={handlePogBoxRightClick}
                onBoxRightClick={handleBoxRightClick}
                showPogs
                showShelfColumnLabels={showShelfColumnLabels}
                showEmbeddingsCount={showEmbeddingsCount}
                pogHeader={(() => {
                  const totalOuts = outsCountPerPog.reduce((a, b) => a + b, 0);
                  const totalBboxes = totalBboxesPerPog.reduce((a, b) => a + b, 0);
                  const matched = totalBboxes - totalOuts;
                  const isComplete = matched >= totalBboxes;
                  return (
                    <span
                      className={`text-base font-normal ${isComplete ? 'text-green-600' : 'text-red-500'}`}
                    >
                      · {matched}/{totalBboxes}
                    </span>
                  );
                })()}
                pogHeaderActions={pogHeaderActions}
                showFallbackImages={true}
              />
            </div>

            <div className="flex mt-1">
              <div className="w-1/2 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-800">Legend:</span>
                <div className="flex items-center gap-1">
                  <span
                    className="inline-block w-4 h-4 rounded border border-gray-400"
                    style={{ backgroundColor: COLORS.SELECTED_BOX }}
                  />
                  <span className="text-xs text-gray-700">Selected</span>
                </div>
                <div className="flex items-center gap-1">
                  <span
                    className="inline-block w-4 h-4 rounded border border-gray-400"
                    style={{ backgroundColor: COLORS.UPDATED_BOX }}
                  />
                  <span className="text-xs text-gray-700">User Matched</span>
                </div>
                <div className="flex items-center gap-1">
                  <span
                    className="inline-block w-4 h-4 rounded border border-gray-400"
                    style={{ backgroundColor: COLORS.MODEL_MATCHED_BOX }}
                  />
                  <span className="text-xs text-gray-700">Model Matched</span>
                </div>
                <div className="flex items-center gap-1">
                  <span
                    className="inline-block w-4 h-4 rounded border border-gray-400"
                    style={{ backgroundColor: COLORS.UNSELECTED_BOX }}
                  />
                  <span className="text-xs text-gray-700">No Match</span>
                </div>
              </div>
              {outsCountPerPog.length > 0 && (
                <div className="w-1/2 flex items-center">
                  {outsCountPerPog.map((count, i) => {
                    const total = totalBboxesPerPog[i] ?? 0;
                    const matched = total - count;
                    const isComplete = matched >= total;
                    return (
                      <div
                        key={i}
                        className={`text-sm font-medium ${isComplete ? 'text-green-600' : 'text-red-500'}`}
                        style={{ width: `${100 / outsCountPerPog.length}%` }}
                      >
                        {matched}/{total}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {detectionForModal && (
              <ImageItemDetailsDialog
                item={detectionForModal.item}
                cameraImageElement={
                  currentImage ? preloadedImages[currentImage.imageUri] : undefined
                }
                isOpen={showDetectionDialog}
                onClose={closeDetectionDialog}
                chainId={chainId}
                storeId={storeId}
                storeName={storeName}
                shouldShowEmbeddings={true}
                cameraImage={cameraImage}
                cartonBoxes={productRecognitionData?.cartonBoxes}
              />
            )}

            {/* Two Column Layout */}
            <div className="mt-4 grid grid-cols-1 max-w-[1000px] gap-4 items-start">
              {/* Left Column */}
              {/* Selected Boxes Info */}
              <div>
                {selectedBoxes.size > 0 && (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="text-green-800 font-medium">
                        {selectedBoxes.size} box
                        {selectedBoxes.size !== 1 ? 'es' : ''} selected
                      </span>
                      <button
                        onClick={() => dispatchSelection({ type: 'CLEAR_SELECTED_BOXES' })}
                        className="px-2 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>
                )}

                {cropScansError && (
                  <div className="text-red-600 text-sm mb-3 p-2 bg-red-50 rounded border">
                    Error: {cropScansError.message}
                  </div>
                )}

                {/* Product Recognition Crop Scans */}
                {productRecognitionData?.inferenceId && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="mb-4">
                      <h3 className="font-medium text-blue-900 mb-1">Matched Items</h3>
                    </div>

                    {sessionStarted && (
                      <>
                        <CropScansList
                          cropScans={filteredCropScans}
                          emptyDescription={
                            'Match unassigned POG item with ROG item by left clicking on them.'
                          }
                          imageUrl={preloadedImage ?? ''}
                          imageId={imageId ? Number(imageId) : undefined}
                          isFetchingCropScans={isFetchingCropScans}
                          isGeneratingCropScans={isGeneratingCropScans}
                          pendingOnly
                          showCompleted
                          previewSize={250}
                          onDelete={handleDeleteCropScan}
                          selectedBoxes={selectedBoxes}
                          pogPreviewsByProductId={pogPreviewsByProductId}
                        />
                        {
                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={() => setShowCreateConfirmation(true)}
                              disabled={isFetchingCropScans || isGeneratingCropScans || !storeName}
                              className="px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isFetchingCropScans
                                ? 'Saving...'
                                : pendingSaves.size > 0
                                  ? `Save corrections (${pendingSaves.size})`
                                  : 'Mark as audited'}
                            </button>
                          </div>
                        }

                        <Dialog
                          open={showCreateConfirmation}
                          onClose={() => {
                            setShowCreateConfirmation(false);
                            setAuditReason('');
                          }}
                          maxWidth="xs"
                          fullWidth
                          PaperProps={{
                            sx: {
                              borderRadius: 3,
                              overflow: 'hidden',
                            },
                          }}
                          slotProps={{
                            backdrop: {
                              sx: {
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                backdropFilter: 'blur(4px)',
                              },
                            },
                          }}
                        >
                          <div
                            style={{
                              height: 4,
                              background:
                                pendingSaves.size > 0
                                  ? 'linear-gradient(90deg, #2196F3, #1976D2)'
                                  : 'linear-gradient(90deg, #43A047, #2E7D32)',
                            }}
                          />
                          <DialogTitle
                            sx={{
                              fontSize: '1.25rem',
                              fontWeight: 700,
                              pt: 3,
                              pb: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            {pendingSaves.size > 0 ? 'Save Corrections' : 'Image Audited'}
                          </DialogTitle>
                          <DialogContent sx={{ pt: 1, pb: 2 }}>
                            <RadioGroup
                              value={auditReason}
                              onChange={(e) => setAuditReason(e.target.value)}
                              sx={{ gap: 0.5 }}
                            >
                              {[
                                { value: 'no_issues', label: 'Reviewed - no issue' },
                                { value: 'no_outs', label: 'No outs' },
                                {
                                  value: UpcCorrectionCanceledReason.OBSTRUCTED,
                                  label: 'Obstructed',
                                },
                                { value: UpcCorrectionCanceledReason.BLURRY, label: 'Blurry' },
                                {
                                  value: UpcCorrectionCanceledReason.MISSING_PRODUCT_BOX,
                                  label: 'Missing product box',
                                },
                              ].map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={
                                    <Radio
                                      size="small"
                                      sx={{
                                        '&.Mui-checked': { color: '#2E7D32' },
                                      }}
                                    />
                                  }
                                  label={option.label}
                                  sx={{
                                    mx: 0,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1.5,
                                    transition: 'background-color 0.15s',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                                    ...(auditReason === option.value && {
                                      backgroundColor: 'rgba(46,125,50,0.08)',
                                    }),
                                    '& .MuiFormControlLabel-label': {
                                      fontSize: '0.9rem',
                                    },
                                  }}
                                />
                              ))}
                            </RadioGroup>
                          </DialogContent>
                          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1.5 }}>
                            <Button
                              onClick={() => {
                                setShowCreateConfirmation(false);
                                setAuditReason('');
                              }}
                              variant="outlined"
                              sx={{
                                flex: 1,
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 1,
                                borderColor: 'divider',
                                color: 'text.primary',
                                '&:hover': {
                                  borderColor: 'text.secondary',
                                  backgroundColor: 'rgba(0,0,0,0.04)',
                                },
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                const canceled =
                                  auditReason !== 'no_outs' && auditReason !== 'no_issues'
                                    ? (auditReason as UpcCorrectionCanceledReason)
                                    : undefined;
                                setShowCreateConfirmation(false);
                                handleCreateEmbeddings(canceled);
                              }}
                              variant="contained"
                              disabled={!auditReason}
                              autoFocus
                              sx={{
                                flex: 1,
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 1,
                                backgroundColor: '#2E7D32',
                                boxShadow: 'none',
                                '&:hover': {
                                  backgroundColor: '#1B5E20',
                                  boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: 'rgba(0,0,0,0.12)',
                                },
                              }}
                            >
                              Confirm
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {(isLoadingImages || isRunningModel) && (
          <PageLoadingSpinner message="Loading camera image..." />
        )}

        {/* Fallback when no images */}
        {!isLoadingImages && !preloadedImage && !isRunningModel && (
          <div className="bg-white rounded-lg shadow-md p-6 flex-1">
            <div className="text-center py-12">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">No Images Available</h2>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutsSelection;
